import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { StorageService } from '../../utils/storage/storage.service';
import { CoreService } from '../core/core.service';
import { WatcherService } from '../../utils/watcher/watcher.service';
import { FILTER_CHANGED, APP_SETTINGS_CHANGED } from '../../utils/watcher/watcher-event';
import { SettingsService } from '../settings/settings.service';
import { AuthService } from '../auth/auth.service';

import filter from 'lodash/filter';
import each from 'lodash/each';
import find from 'lodash/find';
import size from 'lodash/size';
import has from 'lodash/has';
import cloneDeep from 'lodash/cloneDeep';
import flatten from 'lodash/flatten';

export interface IFilterObject {
  label: string;
  data: string;
  toggled: boolean;
  visible: boolean;
}

export interface ISortObject {
  desc_label: string;
  asc_label: string;
  data: string;
  order: 'asc' | 'desc';
  toggled: boolean;
  visible: boolean;
}

export interface IPreorderWeek {
  start: string; // YYYY-MM-DD
  end: string; // YYYY-MM-DD
  filters: IFilterObject[];
}

@Injectable({
  providedIn: 'root',
})
export class DocketFilterService {
  /** List of all supported contexts */
  private _context_filter: IFilterObject[] = [
    {
      label: 'All',
      data: 'all',
      toggled: true,
      visible: false,
    },
    {
      label: 'Delivery',
      data: 'delivery',
      toggled: false,
      visible: false,
    },
    {
      label: 'Table',
      data: 'dinein',
      toggled: false,
      visible: false,
    },
    {
      label: 'Collect',
      data: 'collect_at_counter',
      toggled: false,
      visible: false,
    },
    {
      label: 'Drive up',
      data: 'drive_up',
      toggled: false,
      visible: false,
    },
    {
      label: 'Pickup',
      data: 'onlinepickup',
      toggled: false,
      visible: false,
    },
    {
      label: 'Room Service',
      data: 'room_service',
      toggled: false,
      visible: false,
    },
  ];

  /** List of all available day filters. This is populated dynamically. */
  private _preorder_filter_index: number = 0;
  private _preorders_weeks: IPreorderWeek[] = [];
  private _cached_preorder_weeks: IPreorderWeek[] = [];
  private _cached_preorder_days: IFilterObject[] = [];

  /** List of supported order bump status */
  private _bump_status: IFilterObject[] = [
    {
      label: 'New',
      data: 'new',
      toggled: true,
      visible: true,
    },
    {
      label: 'Current',
      data: 'current',
      toggled: false,
      visible: true,
    },
    {
      label: 'Completed',
      data: 'bumped',
      toggled: false,
      visible: true,
    },
  ];

  /** List of supported order sorting */
  private _order_sorting: ISortObject[] = [
    {
      asc_label: 'Recent',
      desc_label: 'Oldest',
      data: 'eta',
      order: 'desc',
      toggled: true,
      visible: true,
    },
    {
      asc_label: 'Table',
      desc_label: 'Table',
      data: 'table_number',
      order: 'desc',
      toggled: false,
      visible: true,
    },
    // {
    //   label: "Created Date/Time",
    //   data: "created",
    //   toggled: false,
    //   visible: true
    // }
  ];

  /** Fires off on filter change */
  public filterChanged: Subject<string[]> = new Subject();

  /** Flag to only show orders with errors */
  private _onlyShowOrdersWithErrors: boolean = false;

  constructor(
    public storage: StorageService,
    public auth: AuthService,
    public core: CoreService,
    public watcher: WatcherService,
    public appSettings: SettingsService,
  ) {
    this.watcher.on(APP_SETTINGS_CHANGED).subscribe((bump_config: any) => {
      if (size(bump_config)) {
        // YESTERDAY PREORDER FILTER
        if (has(bump_config, 'general') && has(bump_config.general, 'show_yesterday_orders')) {
          if (size(this._cached_preorder_weeks)) {
            const yesterdayFilter: IFilterObject = find(
              this._cached_preorder_weeks[0].filters,
              (filter: IFilterObject) => filter.label == 'Yesterday',
            );
            if (yesterdayFilter) {
              yesterdayFilter.visible = bump_config.general.show_yesterday_orders;
            }
          }

          if (size(this._cached_preorder_days)) {
            const yesterdayFilter: IFilterObject = find(
              this._cached_preorder_days,
              (filter: IFilterObject) => filter.label == 'Yesterday',
            );
            if (yesterdayFilter) {
              yesterdayFilter.visible = bump_config.general.show_yesterday_orders;
            }
          }

          if (size(this._preorders_weeks)) {
            const yesterdayFilter: IFilterObject = find(
              this._preorders_weeks[0].filters,
              (filter: IFilterObject) => filter.label == 'Yesterday',
            );
            if (yesterdayFilter) {
              yesterdayFilter.visible = bump_config.general.show_yesterday_orders;
              this.watcher.broadcast(FILTER_CHANGED);
            }
          }
        }
      }
    });
  }

  /**
   * Updates the available filters and and hides the ones that are not part of the bump screen
   * @param contexts - The list of filters to show
   */
  public showContextFilters(contexts: string[]): void {
    // set everything to visible false
    each(this._context_filter, (context: IFilterObject) => {
      context.visible = context.data == 'all';
    });

    // now, only show what is used
    each(contexts, (context: string) => {
      const filterTarget: IFilterObject = find(this._context_filter, ['data', context]);
      if (filterTarget) filterTarget.visible = true;
    });

    this.fireFilterUpdate();
  }

  changeWeek(direction: 'next' | 'prev'): void {
    if (direction == 'next') {
      this._preorder_filter_index = Math.min(size(this._preorders_weeks) - 1, this._preorder_filter_index + 1);
    } else {
      this._preorder_filter_index = Math.max(0, this._preorder_filter_index - 1);
    }

    this.fireFilterUpdate('preorder', 'weekchange');
  }

  /**
   * Populates the preorder filter with provided dates
   * @param format - The format of string dates
   */
  public createPreorderFilters(
    format: string = 'YYYY-MM-DD',
    emitEvent: boolean = true,
    clearCache: boolean = false,
  ): void {
    let preorderDays: number = this.auth.Active_bump_screen ? this.auth.Active_bump_screen.Max_preorder_days : 1;

    // if max preorder days is less than or equal to 7, then make the calendar filter dynamic
    // based on filter max preorder counts
    if (preorderDays <= 7) {
      // grab selected context
      const contextFilter: IFilterObject = find(
        this._context_filter,
        (filter: IFilterObject) => filter.visible && filter.toggled,
      );
      preorderDays = this.auth.Active_bump_screen
        ? this.auth.Active_bump_screen.getPreorderDaysByFulfillment(contextFilter ? contextFilter.data : '')
        : 1;
    }

    // minimum of 1
    preorderDays = Math.max(1, preorderDays);

    // get the base server time
    const now = this.core.Server_time;
    const todayData: string = now.clone().format(format);

    // check if todayData is NOT equal to filter TODAY, for a re render
    if (!clearCache && size(this._preorders_weeks) > 0 && size(this._preorders_weeks[0].filters) > 0) {
      const todayFilter: IFilterObject = find(
        this._preorders_weeks[0].filters,
        (filter: IFilterObject) => filter.label == 'Today',
      );
      if (todayFilter && todayFilter.data != todayData) {
        clearCache = true;
        emitEvent = true;
      }
    }

    // empty the cache and re render everything
    if (clearCache) {
      this._cached_preorder_weeks = [];
      this._cached_preorder_days = [];
    } else {
      // smarts not to re render

      /** IF SAME NUMBER OF DAYS WILL BE RENDERED */
      let totalDaysRendered: number = 0;
      if (size(this._preorders_weeks) > 0) {
        each(this._preorders_weeks, (week: IPreorderWeek) => {
          totalDaysRendered += size(week.filters);
        });

        totalDaysRendered--; // remove yesterday

        if (preorderDays == totalDaysRendered) {
          // console.log("NO RERENDER REQUIRED");
          return;
        }
      }
    }

    // console.log("PREORDER DAYS TO RENDER -->", preorderDays);

    // ok, remember the selected data
    const selectedIndex: number = this._preorder_filter_index;
    const selectedDate: IFilterObject = find(
      this.Preorder_filters,
      (filter: IFilterObject) => filter.visible && filter.toggled,
    );

    // clear the data to be refreshed
    this._preorder_filter_index = 0;
    this._preorders_weeks = [];

    if (preorderDays <= size(this._cached_preorder_days) - 1) {
      // minus 1 for yesterday
      // Use the cache when the number of items to be rendered is less than the cached days
      // console.log("PREORDER RENDER -> FROM CACHE");
      let filtersCount: number = 0;
      const weeksCount: number = size(this._cached_preorder_weeks);
      const expectedFilterCount: number = preorderDays + 1; // for the yesterday

      for (let index: number = 0; index < weeksCount && filtersCount < expectedFilterCount; index++) {
        let clonedWeek: IPreorderWeek = { ...this._cached_preorder_weeks[index] };
        let filterSize: number = size(clonedWeek.filters);
        if (filterSize > 0) {
          if (filtersCount + filterSize > expectedFilterCount) {
            const excess: number = filtersCount + filterSize - expectedFilterCount;

            // do not use the filtersize because of the yesterday
            clonedWeek.filters = [...clonedWeek.filters].splice(0, size(clonedWeek.filters) - excess);
            filtersCount += size(clonedWeek.filters);
          } else {
            // keep adding
            filtersCount += filterSize;
          }

          this._preorders_weeks.push(clonedWeek);
        }
      }
    } else {
      // Update the list if the number of items to render is more than the cached days
      // console.log("PREORDER RENDER -> NEW");

      // for the first week, add the yesterday and today right away
      let preorderDaysDummy: IFilterObject[] = [];

      // yesterday
      const yesterdayServerTime = now.clone().subtract(1, 'day');
      const yesterdayData: string = yesterdayServerTime.format(format);
      preorderDaysDummy.push({
        label: 'Yesterday',
        data: yesterdayData,
        toggled: false,
        visible: this.appSettings.getBumpScreenSettingByKey('general', 'show_yesterday_orders'),
      });

      // today
      preorderDaysDummy.push({
        label: 'Today',
        data: todayData,
        toggled: false,
        visible: true,
      });

      // if the preorder days is ONLY for today, then don't bother to do the loop
      if (preorderDays > 1) {
        for (let i: number = 1; i < preorderDays; i++) {
          const date = now.clone().add(i, 'day');
          const dateData = date.format(format);
          const weekSize: number = size(this._preorders_weeks);

          preorderDaysDummy.push({
            label: weekSize == 0 ? date.format('ddd') : date.format('MMM D'),
            data: dateData,
            toggled: false,
            visible: true,
          });

          if (
            (weekSize == 0 && size(preorderDaysDummy) == 8) || // first week - including yesterday
            (weekSize > 0 && size(preorderDaysDummy) == 7) || // following weeks - excluding yesterday
            i == preorderDays - 1
          ) {
            // last week

            // set every first day of the week as toggled
            if (size(this._preorders_weeks) > 0) {
              preorderDaysDummy[0].toggled = true;
            }

            const toPush: IPreorderWeek = {
              start: preorderDaysDummy[0].data,
              end: dateData,
              filters: cloneDeep(preorderDaysDummy),
            };

            this._preorders_weeks.push(toPush);

            // start week
            this._cached_preorder_weeks.push(cloneDeep(toPush));

            preorderDaysDummy = [];
          }
        }
      } else {
        // insert the week dummy containing the "Yesterday" and "Today" right away
        const toPush: IPreorderWeek = {
          start: preorderDaysDummy[0].data,
          end: todayData,
          filters: cloneDeep(preorderDaysDummy),
        };

        this._preorders_weeks.push(toPush);

        // start week
        this._cached_preorder_weeks.push(cloneDeep(toPush));
        preorderDaysDummy = [];
      }

      // cached the preorder days
      if (size(this._cached_preorder_weeks)) {
        const days: IFilterObject[][] = this._cached_preorder_weeks.map((week: IPreorderWeek): IFilterObject[] => {
          return week.filters;
        });
        this._cached_preorder_days = [...flatten(days)];
      } else {
        this._cached_preorder_days = [];
      }
    }

    // console.log("PREORDER WEEKS", this._preorders_weeks);
    // console.log("CACHED PREORDER WEEKS", this._cached_preorder_weeks);
    // console.log("CACHED PREORDER DAYS", this._cached_preorder_days);

    // just default to Today for every creation
    if (selectedDate && selectedIndex < size(this._preorders_weeks)) {
      let found: boolean = false;
      let newWeekIndex: number = 0;

      // make sure the preselected data are still available
      each(this._preorders_weeks, (week: IPreorderWeek, index: number) => {
        const filter = find(week.filters, (filter: IFilterObject) => filter.data == selectedDate.data);
        if (filter) {
          found = true;
          newWeekIndex = index;
          return false;
        }
      });

      if (found) {
        this._preorder_filter_index = newWeekIndex;
        this.updateFilterByData('preorder', selectedDate.data, false);
      } else {
        this._preorder_filter_index = 0;
        this.updateFilterByData('preorder', todayData, false);
      }
    } else {
      this._preorder_filter_index = 0;
      this.updateFilterByData('preorder', todayData, false);
    }

    if (emitEvent) this.fireFilterUpdate();
  }

  /**
   * Updates the selected sorting filter
   * @param data - The data property of the sorting filter object to activate
   * @param emitEvent - Flagger to fire the event after changing the value
   */
  updateSelectedSorting(data: string, emitEvent: boolean = true): void {
    const selectedSort: ISortObject = this.getSelectedSorting();
    if (selectedSort.data == data) {
      // flip the order
      selectedSort.order = selectedSort.order == 'asc' ? 'desc' : 'asc';
    } else {
      each(this._order_sorting, (sorting: ISortObject) => {
        sorting.toggled = sorting.data == data;
      });
    }

    this.saveFilters('order sorting');
    if (emitEvent) this.fireFilterUpdate('sort');
  }

  /**
   * Toggle the selected filter
   * @param dataPoint - The type of filter to change. Values are 'context', 'bump', and 'preorder'
   * @param data - The data property of the filter object to activate
   * @param emitEvent - Flagger to fire the event after changing the value
   */
  updateFilterByData(dataPoint: string, data: string, emitEvent: boolean = true): void {
    let filterObj: IFilterObject;
    let filterTarget: IFilterObject[];

    switch (dataPoint) {
      case 'context':
        filterObj = find(this._context_filter, ['data', data]);
        filterTarget = this._context_filter;
        break;
      case 'bump':
        filterObj = find(this._bump_status, ['data', data]);
        filterTarget = this._bump_status;
        break;
      case 'preorder':
        filterObj = find(this.Preorder_filters, ['data', data]);
        filterTarget = this.Preorder_filters;
        break;
    }

    if (filterObj) {
      each(filterTarget, (filter: IFilterObject) => {
        if (filter.data != data) {
          filter.toggled = false;
        }
      });

      filterObj.toggled = true;
      if (emitEvent) this.fireFilterUpdate(dataPoint);

      // save the new filter preferences
      this.saveFilters('bydata - ' + dataPoint);
    }
  }

  /**
   * Update the toggled property of filters by bundle
   * @param dataPoint The type of filter to change. Values are 'context', 'bump', and 'preorder'
   * @param filters The list of of filter objects to update
   * @param emitEvent Flagger to fire the event after changing the value
   */
  updateFiltersByGroup(dataPoint: string, filters: IFilterObject[], emitEvent: boolean = true) {
    let changed: boolean = false;
    let filterObj: IFilterObject[];

    switch (dataPoint) {
      case 'context':
        filterObj = this._context_filter;
        break;
      case 'bump':
        filterObj = this._bump_status;
        break;
      case 'preorder':
        filterObj = this.Preorder_filters;
        break;
    }

    if (filterObj) {
      each(filters, (filter: IFilterObject) => {
        const filterTarget: IFilterObject = find(filterObj, ['data', filter.data]);
        if (filterTarget) {
          changed = changed || filterTarget.toggled != filter.toggled;
          filterTarget.toggled = filter.toggled;
        }
      });

      if (changed && emitEvent) {
        this.fireFilterUpdate(dataPoint);
      }

      // save the new filter preferences
      this.saveFilters('bygroup');
    }
  }

  /**
   * Grabs all the selected filters by type
   */
  public getSelectedFilters(): {
    contexts: IFilterObject[];
    bump_status: IFilterObject[];
    preorder: IFilterObject[];
    order_sorting: ISortObject;
    onlyShowOrdersWithErrors: boolean;
  } {
    return {
      contexts: filter(this._context_filter, { visible: true, toggled: true }),
      bump_status: filter(this._bump_status, { visible: true, toggled: true }),
      preorder: filter(size(this._preorders_weeks) ? this._preorders_weeks[this._preorder_filter_index].filters : [], {
        visible: true,
        toggled: true,
      }),
      order_sorting: find(this._order_sorting, { visible: true, toggled: true }),
      onlyShowOrdersWithErrors: this._onlyShowOrdersWithErrors,
    };
  }

  /**
   * Grab the selected order sorting
   */
  public getSelectedSorting(): ISortObject {
    const sorting: ISortObject = find(this._order_sorting, (sortObj: ISortObject) => {
      return sortObj.visible && sortObj.toggled;
    });

    if (sorting) {
      return sorting;
    } else {
      this._order_sorting[0].toggled = true; // ETA
      return this._order_sorting[0];
    }
  }

  /**
   * Emits a global filter change event
   * @param target The list of target types updated
   */
  public fireFilterUpdate(...target: string[]): void {
    this.filterChanged.next(target);
    this.watcher.broadcast(FILTER_CHANGED, target);
  }

  /**
   * Stores all the toggled filters into storage
   */
  async saveFilters(src: string) {
    const { contexts, bump_status, preorder, order_sorting } = this.getSelectedFilters();
    this.storage.save('context_filters', contexts);
    this.storage.save('bump_filters', bump_status);
    this.storage.save('preorder_filters', preorder);
    this.storage.save('order_filters', order_sorting);
  }

  /**
   * Load all the toggled filter - TODO
   */
  // async loadFilters() {
  //   let contextFilters: IFilterObject[] = await this.storage.load("context_filters");
  //   let bumpFilters: IFilterObject[] = await this.storage.load("bump_filters");
  //   let preorderFilters: IFilterObject[] = await this.storage.load("preorder_filters");

  //   contextFilters = filter(contextFilters, 'toggled');
  //   bumpFilters = filter(bumpFilters, 'toggled');
  //   preorderFilters = filter(preorderFilters, 'toggled');

  //   if(size(contextFilters)) {
  //     const selectedContextData: string = contextFilters[0].data;
  //     each(this._context_filter, (filter: IFilterObject) => {
  //       filter.toggled = filter.visible && filter.data == selectedContextData;
  //     });
  //   }

  //   if(size(bumpFilters)) {
  //     const selectedBumpData: string = bumpFilters[0].data;
  //     each(this._bump_status, (filter: IFilterObject) => {
  //       filter.toggled = filter.visible && filter.data == selectedBumpData;
  //     });
  //   }

  //   if(size(preorderFilters) && size(this._preorder_days)) {
  //     const selectedPreorderData: string = preorderFilters[0].data;
  //     each(this._preorder_days, (filter: IFilterObject) => {
  //       filter.toggled = filter.visible && filter.data == selectedPreorderData;
  //     });
  //   }

  //   this.fireFilterUpdate();
  // }

  public reset(): void {
    // context
    this.updateFilterByData('context', 'all', false);

    // preorder filters - just recreate
    this._preorder_filter_index = 0;
    this._preorders_weeks = [];
    this._cached_preorder_days = [];
    this._cached_preorder_weeks = [];

    // bump status
    this.updateFilterByData('bump', 'new', false);

    // sorting
    each(this._order_sorting, (sorting: ISortObject) => {
      sorting.toggled = sorting.data == 'eta';
      sorting.order = 'desc';
    });

    // errors filter
    this._onlyShowOrdersWithErrors = false;
  }

  public get Context_filters(): IFilterObject[] {
    return this._context_filter;
  }

  public get Bump_filters(): IFilterObject[] {
    return this._bump_status;
  }

  public get Preorder_filters(): IFilterObject[] {
    if (size(this._preorders_weeks)) {
      return this._preorders_weeks[this._preorder_filter_index].filters;
    } else {
      return [];
    }
  }

  public get Preorder_week_index(): number {
    return this._preorder_filter_index;
  }
  public set Preorder_week_index(index: number) {
    this._preorder_filter_index = index;
  }

  public get Preorder_weeks(): IPreorderWeek[] {
    return this._preorders_weeks;
  }

  public get Sorting(): ISortObject[] {
    return this._order_sorting;
  }

  /**
   * Get the onlyShowOrdersWithErrors flag
   */
  public get onlyShowOrdersWithErrors(): boolean {
    return this._onlyShowOrdersWithErrors;
  }

  /**
   * Set the onlyShowOrdersWithErrors flag
   * @param value - The value to set
   * @param emitEvent - Whether to fire a filter update event
   */
  public setOnlyShowOrdersWithErrors(value: boolean, emitEvent: boolean = true): void {
    this._onlyShowOrdersWithErrors = value;
    this.saveFilters('onlyShowOrdersWithErrors');
    if (emitEvent) {
      this.fireFilterUpdate('errors');
    }
  }

  /**
   * Toggle the onlyShowOrdersWithErrors flag
   * @param emitEvent - Whether to fire a filter update event
   */
  public toggleOnlyShowOrdersWithErrors(emitEvent: boolean = true): void {
    this.setOnlyShowOrdersWithErrors(!this._onlyShowOrdersWithErrors, emitEvent);
  }
}
